import { startTransition, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useT } from '@/core/I18nProvider';
import { stripLocalePrefix } from '@/i18n/locale-path';

import { readConsent, writeConsent, type ConsentDecision } from './consent';
import { ConsentBanner } from './ConsentBanner';
import { analytics, initAnalytics } from './posthog-client';
import { setAnalyticsSink } from './track';

import type { PropsWithChildren, ReactElement } from 'react';

function isLocalhost(): boolean {
  return (
    typeof window !== 'undefined' &&
    /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname)
  );
}

/**
 * Wires consent-gated PostHog analytics into the marketing site and renders the
 * consent banner while the visitor is undecided.
 *
 * SSR/hydration safety: the marketing site is statically prerendered and this
 * provider is mounted only in `entry-client` (never `entry-server`). The banner
 * is suppressed until after hydration (`hydrated` flag) so the first client
 * render matches the server-rendered HTML exactly — no hydration mismatch.
 *
 * Localhost (dev / vite preview) never initialises PostHog, so developer
 * traffic is kept out of the shared project even if a key is baked locally.
 */
export function MarketingAnalyticsProvider({ children }: PropsWithChildren): ReactElement {
  const { locale } = useT();
  const location = useLocation();
  const [hydrated, setHydrated] = useState(false);
  const [consent, setConsent] = useState<ConsentDecision | null>(null);
  const started = useRef(false);

  // Read the stored decision only after hydration so the first render is
  // deterministic and matches the prerendered markup. Register the analytics
  // sink so SSR-safe `track()` call sites forward to PostHog (gated on consent).
  // startTransition: this provider sits ABOVE the route <Suspense>, and a sync
  // setState here lands while React 18 is still lazily hydrating the boundary —
  // forcing it to abandon the server HTML and client-render (#418/#422/#423,
  // "Suspense boundary received an update before it finished hydrating").
  // Marking the update non-urgent lets boundary hydration finish first.
  useEffect(() => {
    startTransition(() => {
      setHydrated(true);
      setConsent(readConsent());
    });
    setAnalyticsSink(analytics);
    return () => {
      setAnalyticsSink(null);
    };
  }, []);

  // Initialise on first grant (and on every subsequent route change) → pageview.
  useEffect(() => {
    if (consent !== 'granted') return;
    if (!started.current) {
      const apiKey = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
      const apiHost = import.meta.env.VITE_POSTHOG_HOST as string | undefined;
      initAnalytics({
        apiKey: isLocalhost() ? undefined : apiKey,
        apiHost,
        environment: import.meta.env.PROD ? 'production' : 'development',
        locale,
      });
      started.current = true;
    }
    analytics.capturePageview(location.pathname);
  }, [consent, location.pathname, locale]);

  // Keep the locale super-property current after a language switch.
  useEffect(() => {
    analytics.setLocale(locale);
  }, [locale]);

  const handleAccept = (): void => {
    writeConsent('granted');
    setConsent('granted');
  };
  const handleDecline = (): void => {
    writeConsent('denied');
    setConsent('denied');
    analytics.optOut();
  };

  // The home/landing route is the only dark surface (it suppresses the
  // site-wide light chrome — see App.tsx); everywhere else is light. Tell the
  // banner so it adopts the matching palette instead of glaring against the page.
  const tone = stripLocalePrefix(location.pathname) === '/' ? 'dark' : 'light';

  return (
    <>
      {children}
      {hydrated && consent === null ? (
        <ConsentBanner onAccept={handleAccept} onDecline={handleDecline} tone={tone} />
      ) : null}
    </>
  );
}
